<template>
  <section :id="anchorId" class="stream-entry" :class="{ 'stream-entry--active': active }">
    <!-- Gutter: the continuous version spine, with this release's node on it. -->
    <div class="stream-entry__gutter" aria-hidden="true">
      <span class="stream-entry__node"></span>
    </div>

    <div class="stream-entry__content">
      <header class="stream-entry__header">
        <div class="stream-entry__eyebrow">
          <span v-if="publishedAt" class="stream-entry__date">
            <v-icon size="14" class="stream-entry__date-icon">mdi-calendar-blank-outline</v-icon>
            {{ $formatDate(publishedAt, 'DD MMM YYYY') }}
          </span>
          <span v-if="relativeDate" class="stream-entry__relative">{{ relativeDate }}</span>
          <span v-if="isLatest" class="stream-entry__latest">Latest</span>
        </div>

        <h2 class="stream-entry__title">{{ name }}</h2>

        <p v-if="excerpt" class="stream-entry__excerpt">{{ excerpt }}</p>

        <ul v-if="categories.length" class="stream-entry__categories">
          <li v-for="chip in categories" :key="chip.category" class="stream-entry__category">
            <v-icon size="14" :color="chip.color">{{ chip.icon }}</v-icon>
            <span class="stream-entry__category-count">{{ chip.count }}</span>
            <span>{{ pluralLabel(chip) }}</span>
          </li>
        </ul>
      </header>

      <!-- Body: fetched lazily by the parent as the entry nears the viewport. -->
      <v-skeleton-loader
        v-if="isLoading"
        type="paragraph"
        class="transparent stream-entry__skeleton" />

      <!-- The server returns already-sanitized HTML; it is rendered as-is, exactly as the
           permalink page does. -->
      <div v-else-if="bodyHtml" class="release-body stream-entry__body" v-html="bodyHtml"></div>

      <p v-else-if="hasFailed" class="stream-entry__notice">
        These notes could not be loaded.
        <a class="stream-entry__notice-link" @click="$emit('retry')">Try again</a>
      </p>

      <p v-else-if="isLoaded" class="stream-entry__notice">This release has no notes attached.</p>

      <footer v-if="slug" class="stream-entry__footer">
        <router-link
          class="stream-entry__permalink"
          :to="{ name: 'release-detail-public', params: { slug } }">
          Open this release
          <v-icon size="14" color="primary">mdi-arrow-top-right</v-icon>
        </router-link>
      </footer>
    </div>
  </section>
</template>

<script>
  import dayjs from 'dayjs';
  import relativeTime from 'dayjs/plugin/relativeTime';
  import { $formatDate } from '@Utils';
  import {
    releaseAnchorId,
    releaseBodyHtml,
    releaseCategoryCounts,
    releaseExcerpt,
    releaseName,
    releasePublishedAt,
    releaseSlug,
  } from '@ReleaseNotes/utils/releaseFields';

  dayjs.extend(relativeTime);

  export default {
    name: 'ReleaseStreamEntry',
    props: {
      /** List DTO for this release. The body arrives separately via `body`. */
      release: {
        type: Object,
        required: true,
      },
      /** Position in the loaded list — only used to back-stop a DTO with no slug. */
      index: {
        type: Number,
        default: 0,
      },
      /** True when this is the release the reader is currently on (scroll-spy). */
      active: {
        type: Boolean,
        default: false,
      },
      /** Newest release in the stream — carries the "Latest" marker. */
      isLatest: {
        type: Boolean,
        default: false,
      },
      /**
       * Cache record from `RELEASE_NOTES/GET_TENANT_RELEASE_BODIES`, or null when this entry's
       * body has not been requested yet.
       * @type {{ status: 'loaded'|'failed', data: Object }|null}
       */
      body: {
        type: Object,
        default: null,
      },
      /** True while this entry's body request is in flight. */
      isLoading: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      anchorId() {
        return releaseAnchorId(this.release, this.index);
      },
      name() {
        return releaseName(this.release);
      },
      slug() {
        return releaseSlug(this.release);
      },
      publishedAt() {
        return releasePublishedAt(this.release);
      },
      excerpt() {
        return releaseExcerpt(this.release);
      },
      categories() {
        // Prefer the detail DTO — the list payload may not carry items at all.
        const detail = this.body && this.body.data;
        const fromDetail = releaseCategoryCounts(detail);
        return fromDetail.length ? fromDetail : releaseCategoryCounts(this.release);
      },
      relativeDate() {
        return this.publishedAt ? dayjs(this.publishedAt).fromNow() : '';
      },
      isLoaded() {
        return !!this.body && this.body.status === 'loaded';
      },
      hasFailed() {
        return !!this.body && this.body.status === 'failed';
      },
      bodyHtml() {
        return this.isLoaded ? releaseBodyHtml(this.body.data) : '';
      },
    },
    methods: {
      $formatDate,

      /**
       * "1 Fix" / "3 Fixes". Categories are short nouns, so a trailing "s" is enough — no
       * irregular plurals exist in `ReleaseItemCategoryLabels`.
       * @param {{ label: string, count: number }} chip
       * @returns {string}
       */
      pluralLabel(chip) {
        return chip.count === 1 ? chip.label : `${chip.label}s`;
      },
    },
  };
</script>

<style scoped lang="scss">
  .stream-entry {
    display: grid;
    grid-template-columns: 32px minmax(0, 1fr);
    column-gap: var(--ds-space-md);
  }

  // --- The version spine -----------------------------------------------------
  // One hairline runs the full height of every entry, so the stream reads as a single
  // continuous thread rather than a stack of cards. The node sits on the title's optical
  // centre and fills solid when the reader is on this release.
  .stream-entry__gutter {
    position: relative;
    display: flex;
    justify-content: center;
  }

  .stream-entry__gutter::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: var(--v-bg3-base, rgba(127, 127, 127, 0.28));
  }

  // The thread starts at the first node and stops at the last, so it never dangles into the
  // page header or past the end of the stream.
  .stream-entry:first-child .stream-entry__gutter::before {
    top: 14px;
  }

  // `:last-of-type`, not `:last-child` — the "show earlier releases" block is the real last
  // child of the stream, so `:last-child` would never match an entry.
  .stream-entry:last-of-type .stream-entry__gutter::before {
    bottom: auto;
    height: 22px;
  }

  .stream-entry__node {
    position: absolute;
    top: 8px;
    width: 11px;
    height: 11px;
    border-radius: var(--ds-radius-full);
    border: 2px solid var(--v-bg3-base, rgba(127, 127, 127, 0.5));
    background: var(--v-bg1-base, #ffffff);
    transition:
      background-color var(--ds-transition-default),
      border-color var(--ds-transition-default),
      box-shadow var(--ds-transition-default);
  }

  .stream-entry--active .stream-entry__node {
    background: var(--v-primary-base);
    border-color: var(--v-primary-base);
    box-shadow: 0 0 0 4px rgba(44, 194, 149, 0.18);
  }

  // --- Content ---------------------------------------------------------------
  .stream-entry__content {
    min-width: 0;
    padding-bottom: var(--ds-space-2xl);
  }

  .stream-entry__header {
    margin-bottom: var(--ds-space-md);
  }

  .stream-entry__eyebrow {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--ds-space-sm);
    font-size: var(--ds-font-caption);
    line-height: 1.5;
    color: var(--v-text_secondary-base);
  }

  // The publish date is the entry's primary orientation cue on a page of stacked releases, so it
  // is pulled up to body size and full-strength text — the eyebrow's muted colour applies to the
  // relative time beside it, which is the supporting detail.
  .stream-entry__date {
    display: inline-flex;
    align-items: center;
    gap: var(--ds-space-xs);
    font-size: var(--ds-font-body);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
    color: var(--v-text-base);
  }

  .stream-entry__date-icon {
    color: var(--v-primary-base);
  }

  .stream-entry__relative {
    opacity: 0.7;
  }

  .stream-entry__relative::before {
    content: '·';
    margin-right: var(--ds-space-sm);
  }

  .stream-entry__latest {
    padding: 1px var(--ds-space-sm);
    border: 1px solid var(--v-primary-base);
    border-radius: var(--ds-radius-full);
    color: var(--v-primary-base);
    font-size: var(--ds-font-micro);
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .stream-entry__title {
    margin: var(--ds-space-xs) 0 0;
    font-family: var(--ds-font-family-heading);
    font-size: var(--ds-font-display);
    font-weight: 600;
    line-height: 1.25;
    // Version names are mostly numerals; tabular figures keep them aligned with the rail.
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
    color: var(--v-text-base);
  }

  .stream-entry__excerpt {
    margin: var(--ds-space-sm) 0 0;
    font-size: var(--ds-font-body);
    line-height: 1.6;
    color: var(--v-text_secondary-base);
  }

  .stream-entry__categories {
    display: flex;
    flex-wrap: wrap;
    gap: var(--ds-space-sm);
    margin: var(--ds-space-md) 0 0;
    padding: 0;
    list-style: none;
  }

  .stream-entry__category {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px var(--ds-space-sm);
    border: 1px solid var(--v-bg3-base, rgba(127, 127, 127, 0.28));
    border-radius: var(--ds-radius-full);
    font-size: var(--ds-font-caption);
    color: var(--v-text_secondary-base);
    white-space: nowrap;
  }

  .stream-entry__category-count {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--v-text-base);
  }

  .stream-entry__skeleton {
    margin-top: var(--ds-space-md);
  }

  .stream-entry__notice {
    margin: var(--ds-space-md) 0 0;
    font-size: var(--ds-font-body-sm);
    color: var(--v-text_secondary-base);
  }

  .stream-entry__notice-link {
    color: var(--v-primary-base);
    cursor: pointer;
  }

  .stream-entry__footer {
    margin-top: var(--ds-space-md);
  }

  .stream-entry__permalink {
    display: inline-flex;
    align-items: center;
    gap: var(--ds-space-xs);
    font-size: var(--ds-font-body-sm);
    font-weight: 500;
    color: var(--v-primary-base);
    text-decoration: none;
  }

  .stream-entry__permalink:hover,
  .stream-entry__permalink:focus-visible {
    text-decoration: underline;
  }

  .stream-entry__permalink:focus-visible {
    outline: var(--ds-focus-ring-width) solid var(--ds-focus-ring-color);
    outline-offset: var(--ds-focus-ring-offset);
    border-radius: var(--ds-radius-sm);
  }

  @media (max-width: 600px) {
    .stream-entry {
      grid-template-columns: 20px minmax(0, 1fr);
      column-gap: var(--ds-space-sm);
    }

    .stream-entry__title {
      font-size: var(--ds-font-title);
    }

    .stream-entry__content {
      padding-bottom: var(--ds-space-xl);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .stream-entry__node {
      transition: none;
    }
  }
</style>
